import React, { useState } from "react";
import { motion } from "framer-motion";
import { Scissors, Music, Wand2, Play, Info, HelpCircle } from "lucide-react";
import GameInstructionsModal from "./ui/GameInstructionsModal";

const LAYERS = [
  { id: "video", label: "Visual Narrative", color: "#c9a961", icon: Play },
  { id: "audio", label: "Soundscape", color: "#b87333", icon: Music },
  { id: "vfx", label: "Atmospherics", color: "#e4c77a", icon: Wand2 }
];

const EditWorkshop = () => {
  const [activeLayer, setActiveLayer] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);

  return (
    <section id="workshop" className="py-32 relative overflow-hidden bg-[var(--ink)]">
      <div className="container-xl px-6 relative z-10">
        <div className="flex flex-col lg:flex-row gap-20 items-center">
          
          {/* Left: The Timeline */}
          <div className="flex-1 w-full order-2 lg:order-1">
            <div className="p-8 rounded-3xl bg-[var(--ink-2)] border border-[var(--rule)] shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between mb-10">
                <h3 className="font-display text-2xl text-[var(--cream)]">Timeline Studio</h3>
                <div className="px-3 py-1 bg-white/5 rounded-full font-impact text-[10px] tracking-widest text-[var(--muted)]">00:12:44:02</div>
              </div>

              {/* Timeline Tracks */}
              <div className="space-y-4">
                {LAYERS.map((layer) => (
                  <div key={layer.id} className="relative h-16 group">
                    <div className="absolute left-0 top-0 bottom-0 w-32 bg-white/5 rounded-l-xl border-r border-white/10 flex items-center px-4 gap-3 z-10">
                      <layer.icon size={16} className={activeLayer === layer.id ? "text-[var(--gold)]" : "text-[var(--muted)]"} />
                      <span className="font-impact text-[10px] tracking-widest text-[var(--muted)] uppercase">{layer.id}</span>
                    </div>
                    <div className="absolute left-32 right-0 top-0 bottom-0 bg-white/2 overflow-hidden rounded-r-xl">
                      <motion.div 
                        initial={{ x: "-100%" }}
                        animate={{ x: activeLayer === layer.id ? "0%" : "-40%" }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        className="h-full w-full"
                        style={{ background: `linear-gradient(90deg, transparent, ${layer.color}20, transparent)` }}
                      />
                      {/* Placeholder Clips */}
                      <div className="absolute inset-4 flex gap-2">
                        {[1, 2, 3, 4, 5].map(i => (
                          <motion.div 
                            key={i}
                            whileHover={{ scaleY: 1.1, backgroundColor: layer.color }}
                            onClick={() => setActiveLayer(layer.id)}
                            className="h-full w-20 rounded-md border border-white/5 cursor-pointer"
                            style={{ 
                              background: activeLayer === layer.id ? layer.color : "rgba(255,255,255,0.05)",
                              opacity: i % 2 === 0 ? 0.8 : 0.4
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Playhead */}
              <div className="mt-6 relative h-2 bg-white/5 rounded-full">
                <motion.div 
                  animate={{ left: ["0%", "100%", "0%"] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                  className="absolute top-1/2 -translate-y-1/2 w-0.5 h-12 bg-[var(--gold)] shadow-[0_0_10px_var(--gold)] z-20"
                />
              </div>
            </div>
            
            <p className="mt-8 text-[13px] text-[var(--muted)] italic flex items-center gap-2 justify-center">
              <Info size={14} /> Interactive Timeline: Select clips to see how layers weave into stories.
            </p>
          </div>

          {/* Right: Content */}
          <div className="max-w-md order-1 lg:order-2">
            <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-[11px] mb-4 block">Interactive Editing</span>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--cream)] mb-8 leading-tight">
              Weaving the <span className="italic gold-text">Threads</span> of Time
            </h2>
            <p className="text-[var(--cream-muted)] text-lg mb-10 font-light leading-relaxed">
              Experience the rhythmic art of assembly. Our editors don't just cut clips; they synchronize emotion, sound, and visual impact.
            </p>
            <div className="flex flex-col gap-4">
              <button 
                onClick={() => setShowInstructions(true)}
                className="btn-ghost flex items-center justify-center gap-2 text-[10px] tracking-widest"
              >
                <HelpCircle size={14} /> HOW TO USE STUDIO
              </button>
              <button className="btn-primary w-full lg:w-auto">
                Request Studio Tour
              </button>
            </div>
          </div>

        </div>
      </div>

      <GameInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="Timeline Studio Lab"
        instructions={[
          { title: "Select a Clip", desc: "Click on any clip in the timeline tracks (Visual, Soundscape, or Atmospherics) to see how it integrates into the project." },
          { title: "Layer Synchrony", desc: "Selecting a clip triggers the 'Layer Synchrony' effect, visualizing the data flow and emotional impact of that specific edit." },
          { title: "Monitor the Playhead", desc: "Watch the golden playhead as it moves across the timeline, showing the real-time progression of the cinematic story." },
          { title: "Understand the Weave", desc: "Each layer adds a unique dimension. Experiment with different clips to understand how we weave complex narratives together." }
        ]}
      />
    </section>
  );
};

export default EditWorkshop;
